export default {
  id: 'order-types',
  title: 'Order Types',
  content: `## Order Types

TradeLab supports two order types: **Market** and **Limit**. Understanding how each works in the simulator is important for realistic practice.

---

## Market Order

A Market order fills immediately at the **last fetched price** for that stock.

| Property | Value |
|---|---|
| Fill timing | Immediate on placement |
| Fill price | Last fetched price from the price feed |
| Use case | When you want guaranteed entry and speed matters |

**When to use:**
- You want to enter or exit right now without waiting
- The stock is moving quickly and you do not want to risk missing the move
- You are closing a position at end of day

**Limitation:** The fill price is the last fetched price, not a real-time tick. In a fast-moving stock, this may differ slightly from what you see on the chart.

---

## Limit Order

A Limit order sits in the queue and fills only when the stock price **reaches your specified limit price**.

| Property | Value |
|---|---|
| Fill timing | When market price reaches your limit price |
| Fill price | Your specified limit price |
| Use case | When you want a specific entry or exit price |

**How it works in TradeLab:**
1. You place a Limit order at ₹490
2. The order status becomes **OPEN**
3. Capital is reserved from your cash balance
4. Every minute, the system checks if the current fetched price ≤ ₹490 (for a buy)
5. When the condition is met, the order fills at ₹490

**When to use:**
- You have identified a specific entry level from your chart analysis
- You do not want to chase the price — you want the price to come to you
- This is the recommended order type for disciplined traders

---

## Order Statuses

Every order moves through a defined lifecycle:

| Status | What it means |
|---|---|
| QUEUED | Order created before market hours — waiting for 9:15 AM to activate |
| OPEN | Active in the queue — waiting for price to reach your limit |
| EXECUTED | Order has filled — position is now live |
| CANCELLED | Order was cancelled before filling |

---

## Trade Types

Separate from order type, every trade has a **Trade Type**:

| Trade Type | Description |
|---|---|
| INTRADAY | Same-day trade — auto-exited at 3:10 PM IST if still open |
| DELIVERY | Multi-day swing trade — holds overnight until you manually exit |

Choose INTRADAY for same-day setups. Choose DELIVERY for swing trades you plan to hold for days or weeks.

---

## Stop Loss and Target

When placing any order, you can optionally set a **Stop Loss** and **Target** price.

If set, TradeLab monitors your position every minute during market hours:
- If the price drops to or below your Stop Loss → position is automatically exited
- If the price rises to or above your Target → position is automatically exited

> Setting Stop Loss and Target at order time is strongly recommended. It removes the emotional decision of when to exit and enforces your original plan.`,
};
